import { Actor } from "./actor";
import { Text } from "./text";
import { EVENTS_NAME, GameStatus } from "../consts.ts";
import { Input } from "phaser";
import { DEFAULT_PLAYER_START_LOCATION } from "../consts.ts";
import { EventBus } from "../EventBus.ts";

export class Player extends Actor {
  private keyW: Phaser.Input.Keyboard.Key;
  private keyA: Phaser.Input.Keyboard.Key;
  private keyS: Phaser.Input.Keyboard.Key;
  private keyD: Phaser.Input.Keyboard.Key;
  private playerBanner: Text;
  private statLine: Text;
  private healthBar: Phaser.GameObjects.Graphics;
  // Health has no server-side max and testers run "overfull" (e.g. 500), so the
  // bar scales to the highest value seen (floor 100) — a topped-up player reads
  // as full instead of pinning at 100%.
  private maxHealthSeen = 100;
  private keySpace: Input.Keyboard.Key;
  public shake: any;

  // ── Fuel-driven movement (alcohol = fuel) ──────────────────────────────
  // Speed scales from BASE (full tank) down toward MIN (empty); every pixel
  // travelled burns fuel = distance / FUEL_BURN_DIVISOR. Tune these to taste.
  BASE_VELOCITY = 220; // px/s at full fuel
  MIN_VELOCITY = 70; // px/s floor (non-zero) when out of fuel
  MAX_ALCOHOL = 100; // full tank

  // Effective speed, consumed by WASD (here) and click-to-move (BaseScene).
  get VELOCITY(): number {
    const t = Math.max(0, Math.min(1, this.alcohol / this.MAX_ALCOHOL));
    return this.MIN_VELOCITY + (this.BASE_VELOCITY - this.MIN_VELOCITY) * t;
  }

  /** Best known max health (high-water mark, floor 100). Used for damage
   *  expressed as a fraction of the health bar — see Monster.touchDamageFor. */
  getMaxHealth(): number {
    return Math.max(this.maxHealthSeen, this.health);
  }

  score = 0;
  avatar_id = 0;
  account_name = "";
  player1: boolean = false;
  // Last position reported via the playerMove event (local player only).
  private lastEmittedX = Number.NaN;
  private lastEmittedY = Number.NaN;
  // Whether we've adopted the server's saved position yet (local player only).
  private serverPositionSynced = false;
  // Kept so the listener can be unhooked on destroy: EventBus outlives the
  // scene, and a stale listener on a destroyed Player throws — which aborts
  // the whole emit chain and silently kills every listener after it.
  private onPlayerDied?: (info: any) => void;
  // Click-to-move / remote-path tween chain for THIS character.
  //
  // ⚠️ This used to be a single scene-level field on BaseScene, shared by
  // player1 and every remote player — and `moveCharacter` stops whatever is in
  // it before starting a new chain. So ANY remote player moving killed your
  // own click-to-move mid-path. It went unnoticed while the world was empty;
  // once AI NPCs and simulated players started wandering and broadcasting
  // every second, a click would run for about a tick and then just stop.
  public moveTweenChain: Phaser.Tweens.TweenChain | null = null;

  constructor(
    scene: Phaser.Scene,
    account_name: string,
    avatar_id: number,
    x: number,
    y: number,
    score: number,
    alcohol: number,
    health: number,
    player1: boolean = false,
  ) {
    super(scene, x, y, `king-${avatar_id}`);
    this.avatar_id = avatar_id;
    this.account_name = account_name;
    this.score = score;
    this.alcohol = alcohol;
    this.health = health;
    this.player1 = player1;
    // KEYS
    if (this.player1) {
      if (this && this.scene && this.scene.input && this.scene.input.keyboard) {
        this.keyW = this.scene.input.keyboard.addKey("W", false);
        this.keyA = this.scene.input.keyboard.addKey("A", false);
        this.keyS = this.scene.input.keyboard.addKey("S", false);
        this.keyD = this.scene.input.keyboard.addKey("D", false);
      }
      if (this.keyW) {
        this.keyW.on("up", (_event: KeyboardEvent) => {
          this.setVelocity(0);
          this.direction = "up";
        });
      }
      if (this.keyA) {
        this.keyA.on("up", (_event: KeyboardEvent) => {
          this.setVelocity(0);
          this.direction = "left";
        });
      }
      if (this.keyS) {
        this.keyS.on("up", (_event: KeyboardEvent) => {
          this.setVelocity(0);
          this.direction = "down";
        });
      }
      if (this.keyD) {
        this.keyD.on("up", (_event: KeyboardEvent) => {
          this.setVelocity(0);
          this.direction = "right";
        });
      }
    }
    // PHYSICS
    this.getBody().setSize(16, 32);
    this.getBody().setOffset(8, 0);
    // HP
    // Overlay stack, bottom-up: sprite / green health bar / stats / name.
    this.playerBanner = new Text(this.scene, this.x, this.y, this.account_name)
      .setFontSize(14);
    this.playerBanner.setOrigin(0.5, 1);
    this.playerBanner.setDepth(1000);
    this.statLine = new Text(this.scene, this.x, this.y, this.statText())
      .setFontSize(13);
    this.statLine.setOrigin(0.5, 1);
    this.statLine.setDepth(1000);
    this.healthBar = this.scene.add.graphics();
    this.healthBar.setDepth(1000);
    this.drawOverlay();
    // Defense
    if (this.player1) {
      if (this.scene.input.keyboard) {
        this.keySpace = this.scene.input.keyboard.addKey(32, false);
        this.keySpace.on("down", (_event: KeyboardEvent) => {
          this.anims.play(this.avatarAnim(`attack-${this.direction}`), true);
          this.scene.game.events.emit(EVENTS_NAME.attack);
        });
      }
      // Mobile has no spacebar: DOUBLE-TAP attacks. (The second tap also
      // re-targets click-to-move, which is fine — you lunge at what you hit.)
      let lastTapMs = 0;
      this.scene.input.on("pointerdown", () => {
        const now = this.scene.time.now;
        if (now - lastTapMs < 300) {
          this.anims.play(this.avatarAnim(`attack-${this.direction}`), true);
          this.scene.game.events.emit(EVENTS_NAME.attack);
        }
        lastTapMs = now;
      });
    }
    // Register this avatar's animations for EVERY player (local and remote):
    // remote players animate too, and each needs its own avatar's frames.
    this.initAnimations();
    // Handle game restart
    this.on("destroy", () => {
      // keySpace only exists for the local player; remote players despawn too.
      this.keySpace?.removeAllListeners();
      // Out-of-range despawn can destroy a remote player mid-path; a chain
      // still tweening a destroyed sprite throws on the next update.
      if (this.moveTweenChain) {
        this.moveTweenChain.stop();
        this.moveTweenChain = null;
      }
      if (this.onPlayerDied) {
        EventBus.removeListener(EVENTS_NAME.playerDied, this.onPlayerDied);
        this.onPlayerDied = undefined;
      }
    });
    if (this.player1) {
      this.scene.game.events.on(
        EVENTS_NAME.chestLoot,
        () => {
          this.health += 10;
        },
        this,
      );
      EventBus.on(EVENTS_NAME.engageLaptop, () => {
        this.anims.play(this.avatarAnim("laptop"), true);
      });
      EventBus.on(EVENTS_NAME.playerInfo, (playerInfo: any) => {
        this.health = playerInfo.health;
        // Fuel is server-authoritative (burned on PlayerMove + persisted); use it.
        this.alcohol = playerInfo.alcohol;
        this.score = playerInfo.score;
        // One-time: adopt the server's saved position on the first sync after
        // creation. The scene may have been built from an account with no
        // position (fresh login after a session reset), placing us at the
        // default spawn — without this snap we'd then report the spawn back
        // and clobber the real saved position on disk.
        if (!this.serverPositionSynced && playerInfo.position) {
          this.serverPositionSynced = true;
          this.setPosition(
            playerInfo.position.x_pos,
            playerInfo.position.y_pos,
          );
          // Server already knows this position — don't re-report it.
          this.lastEmittedX = this.x;
          this.lastEmittedY = this.y;
        }
        this.update();
      });
      // WASTED. Server-authoritative: it has already respawned us (full
      // health, 25 fuel, Impound Lot) and sent the post-respawn stats here,
      // so trust them rather than the health value — by the time this
      // arrives the 0 is long gone. See PlayerDied in client_messages.rs.
      this.onPlayerDied = (info: any) => {
        if (!this.active || !this.scene) return; // stale listener after restart
        this.health = info?.health ?? this.health;
        this.alcohol = info?.alcohol ?? this.alcohol;
        this.setPosition(
          info?.respawn_position?.x_pos ?? DEFAULT_PLAYER_START_LOCATION[0],
          info?.respawn_position?.y_pos ?? DEFAULT_PLAYER_START_LOCATION[1],
        );
        // Don't echo the teleport back — the server chose it.
        this.lastEmittedX = this.x;
        this.lastEmittedY = this.y;
        this.drawOverlay();
        this.scene.game.events.emit(EVENTS_NAME.gameEnd, GameStatus.LOSE);
      };
      EventBus.on(EVENTS_NAME.playerDied, this.onPlayerDied);
    }
  }

  update(): void {
    this.drawOverlay();
    if (this.body && this.account_name) {
      // are two keys down?
      let velFactor = 1;
      if (
        (this.keyW?.isDown && this.keyA?.isDown) ||
        (this.keyW?.isDown && this.keyD?.isDown) ||
        (this.keyS?.isDown && this.keyA?.isDown) ||
        (this.keyS?.isDown && this.keyD?.isDown)
      ) {
        velFactor = Math.sqrt(2) / 2;
      }
      if (this.keyW?.isDown) {
        this.body.velocity.y = -this.VELOCITY * velFactor;
        this.direction = "up";
      }
      if (this.keyA?.isDown) {
        this.body.velocity.x = -this.VELOCITY * velFactor;
        this.direction = "left";
      }
      if (this.keyS?.isDown) {
        this.body.velocity.y = this.VELOCITY * velFactor;
        this.direction = "down";
      }
      if (this.keyD?.isDown) {
        this.body.velocity.x = this.VELOCITY * velFactor;
        this.direction = "right";
      }

      // If no keys are pressed but the player is still moving (click mode),
      // update the direction based on velocity
      if (
        !this.keyW?.isDown &&
        !this.keyA?.isDown &&
        !this.keyS?.isDown &&
        !this.keyD?.isDown
      ) {
        this.updateDirectionFromVelocity();
      }
      if (this.body.velocity.x !== 0 || this.body.velocity.y !== 0) {
        this.anims.play(this.avatarAnim(`run-${this.direction}`), true);
      }
      // Report movement by POSITION delta, not velocity: click-to-move tweens
      // change x/y directly (velocity stays 0), so velocity-gated reporting
      // missed entire paths. The PhaserGame listener throttles the actual
      // websocket sends. Local player only.
      if (
        this.player1 &&
        (this.x !== this.lastEmittedX || this.y !== this.lastEmittedY)
      ) {
        this.lastEmittedX = this.x;
        this.lastEmittedY = this.y;
        EventBus.emit(EVENTS_NAME.playerMove, {
          zone: 1,
          x: this.x,
          y: this.y,
        });
      }
    }
  }

  /** Fuel + score, sized to read at a glance. */
  private statText(): string {
    return `${Math.floor(this.alcohol)}🍺  ${this.score}✦`;
  }

  /** Name above the sprite. Health bar and fuel/score are LOCAL-ONLY.
   *
   *  Showing another character's health or fuel hands away the whole
   *  "spot the AI" mechanic. AI mobs never take damage -- touch damage is
   *  victim-authoritative, so it only ever applies to your own player -- and
   *  they never burn fuel, because `burn_fuel` is called from the PlayerMove
   *  handler and the server's AI wander pass does not go through it. So an
   *  AI's bar sits at full and its fuel is frozen at a constant, forever,
   *  while every human's visibly ticks down as they move and fight. A player
   *  could unmask the entire roster by standing still and watching numbers.
   *
   *  The NAME stays visible: you need it to make a guess at all. */
  private drawOverlay(): void {
    if (this.health > this.maxHealthSeen) {
      this.maxHealthSeen = this.health;
    }
    const top = this.y - this.displayHeight / 2; // sprite top edge
    const barW = 52;
    const barH = 7;
    // Flush with the sprite's top edge rather than floating above it: several
    // avatars are short figures inside the same 32x64 frame, so a gap here read
    // as the banner hovering well above their head.
    const barY = top;
    const x0 = this.x - barW / 2;
    this.healthBar.clear();
    if (this.player1) {
      const frac = Math.max(0, Math.min(1, this.health / this.maxHealthSeen));
      this.healthBar.fillStyle(0x06240f, 0.85);
      this.healthBar.fillRect(x0, barY, barW, barH);
      this.healthBar.fillStyle(0x35e05f, 1);
      this.healthBar.fillRect(x0, barY, barW * frac, barH);
      this.healthBar.lineStyle(1, 0x000000, 0.9);
      this.healthBar.strokeRect(x0, barY, barW, barH);
      this.statLine.setText(this.statText());
      this.statLine.setPosition(this.x, barY - 2);
    } else {
      this.statLine.setText("");
    }
    // Remote characters lose the bar+stats row entirely, so the name sits
    // where the bar would have been rather than floating with a gap.
    const nameY = this.player1 ? barY - 2 - this.statLine.height : barY - 2;
    this.playerBanner.setText(this.account_name);
    this.playerBanner.setPosition(this.x, nameY);
  }

  public animateDamage(value?: number): void {
    super.animateDamage(value);
    // Local decrement is for RESPONSIVENESS only (the authoritative health
    // arrives on PlayerInfo ~1s later). Death is NOT decided here:
    //
    //  1. `Actor.animateDamage` applies `health -= value` inside the tween's
    //     `onStart` callback, which runs on the next tween update — so a
    //     `this.health <= 0` test right here reads the PRE-hit value and is
    //     always one hit stale.
    //  2. Even when it did fire, the server had already respawned us to full
    //     health and the next PlayerInfo overwrote the 0 — the bar dropped,
    //     popped back up, and WASTED never showed.
    //
    // The server now sends an explicit PlayerDied; see the handler in the
    // player1 block below.
    this.drawOverlay();
  }

  /**
   * Presentational-only recoil. NO health change, nothing sent to the server.
   *
   * Exists to close a "spot the AI" tell rather than to model combat. AI mobs
   * ride the remote-player path, and AI mobs never take damage — so a player
   * who swung at a suspected bot and saw literally nothing happen had a free,
   * silent, 100%-accurate detector. Now every remote character recoils when
   * you connect, human or synthetic, and the swing tells you nothing.
   *
   * Symmetry is the requirement here: making ONLY AIs flinch would just
   * invert the tell.
   */
  public flinch(): void {
    if (!this.active || !this.scene) {
      return;
    }
    this.setTintFill(0xffffff);
    this.scene.time.delayedCall(70, () => {
      if (this.active) this.setTint(0xff8888);
    });
    this.scene.time.delayedCall(190, () => {
      if (this.active) this.clearTint();
    });
  }

  /** Animation key for THIS player's avatar (see initAnimations). */
  avatarAnim(name: string): string {
    return `p${this.avatar_id}-${name}`;
  }

  private initAnimations(): void {
    // Phaser animations are GLOBAL to the scene: a shared key like "run-down"
    // carries the frames of whichever avatar registered it first, so every
    // OTHER player who played it got re-skinned to that avatar (the "remote
    // players wear my sprite" bug). Namespace each key by avatar id and
    // register per instance; guard so two players with the same avatar don't
    // double-register.
    const atlas = `a-player-${this.avatar_id}`;
    const defs: Array<{ name: string; end: number }> = [
      { name: "run-right", end: 5 },
      { name: "attack-right", end: 5 },
      { name: "run-up", end: 5 },
      { name: "attack-up", end: 5 },
      { name: "run-down", end: 5 },
      { name: "attack-down", end: 5 },
      { name: "run-left", end: 5 },
      { name: "attack-left", end: 5 },
      { name: "laptop", end: 11 },
    ];
    for (const d of defs) {
      const key = this.avatarAnim(d.name);
      if (!this.scene.anims.exists(key)) {
        this.scene.anims.create({
          key,
          frames: this.scene.anims.generateFrameNames(atlas, {
            prefix: `${d.name}-`,
            end: d.end,
          }),
          frameRate: 8,
        });
      }
    }
  }

  destroy(fromScene?: boolean): void {
    if (this.player1) {
      EventBus.removeListener(EVENTS_NAME.engageLaptop);
      EventBus.removeListener(EVENTS_NAME.playerInfo);
    }
    // The banner is a separate game object — without this it lingers after
    // the sprite is gone. And super.destroy() was never called here before,
    // so "destroyed" players actually stayed in the scene forever.
    this.playerBanner?.destroy();
    this.statLine?.destroy();
    this.healthBar?.destroy();
    super.destroy(fromScene);
  }
}
